import { useCallback, useContext, useEffect, useState } from 'react';
import * as R from 'ramda';

import {
  useGoogleMapsStateUpdater,
  useGoogleMapsStateValue,
  withGoogleMapsState,
} from '@atom/components/common/map/hooks/googleMapsStateHook';
import Map from '@atom/components/common/map/Map';
import MapMarkers from '@atom/components/common/map/MapMarkers';
import MapPinMarker from '@atom/components/common/map/markers/MapPinMarker';
import { SearchParamType, useNavigation } from '@atom/hooks/useNavigation';
import { Progress } from '@atom/mui';
import { InventoryMapFilters } from '@atom/types/map';
import {
  getGeoJsonFromMapBounds,
  urlValueToLatLng,
} from '@atom/utilities/mapUtilities';

import InventoryPortalContext from '../InventoryPortalContext';

import { useInventoryMapData } from './hooks/inventoryMapDataHook';

interface Props {
  filters: InventoryMapFilters;
}

const InventoryMap = ({ filters }: Props) => {
  const { activeCategory } = useContext(InventoryPortalContext);

  const { isLoaded } = useGoogleMapsStateValue();
  const dispatch = useGoogleMapsStateUpdater();

  const [map, setMap] = useState<google.maps.Map>(null);
  const [coordinates, setCoordinates] = useState<string>(null);

  useEffect(() => {
    dispatch({
      type: 'SET',
      payload: {
        key: 'grab',
        data: false,
      },
    });
  }, []);

  const [{ center, zoom, searchPoint }, { updateSearchParams }] = useNavigation(
    {
      center: SearchParamType.STRING,
      zoom: SearchParamType.NUMBER,
      searchPoint: SearchParamType.STRING,
    },
  );

  const params = {
    coordinates,
    zoomLevel: zoom,
    ...(activeCategory.id !== 'inventory' && {
      categoryIds: activeCategory.id,
    }),
    ...filters,
  };

  const [mapData, loading] = useInventoryMapData(params);
  const onLoad = useCallback(setMap, []);

  const onIdle = useCallback(() => {
    if (
      !R.equals(map.getZoom(), zoom) ||
      !R.equals(map.getCenter().toUrlValue(), center) ||
      !coordinates
    ) {
      updateSearchParams({
        zoom: map.getZoom(),
        center: map.getCenter().toUrlValue(),
      });
      R.pipe(
        getGeoJsonFromMapBounds,
        R.prop('coordinates'),
        JSON.stringify,
        setCoordinates,
      )(map);
    }
  }, [map, center, zoom, searchPoint, coordinates]);

  return !isLoaded ? (
    <Progress style={{ height: '100%' }} />
  ) : (
    <Map
      onIdle={onIdle}
      onLoad={onLoad}
      center={center && urlValueToLatLng(center)}
      zoom={zoom}
      loading={loading}
      mapStyleControl
      mapSearchBox
      layerControls
    >
      <MapMarkers data={mapData} />
      {searchPoint && <MapPinMarker center={searchPoint} />}
    </Map>
  );
};

export default withGoogleMapsState<Props>(InventoryMap);
